import { useState, useEffect, useCallback } from 'react';
import nocobaseService from '../services/nocobaseService';
import { useSubservices, useServices } from './useAirtable';

/**
 * Hook for fetching service details for a sub-service
 * @param {number|string} subserviceId - The ID of the sub-service
 * @param {string} language - Language code ('en' or 'ar')
 * @returns {Object} - { details, loading, error, refetch }
 */
export const useServiceDetails = (subserviceId, language = 'en') => {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDetails = useCallback(async () => {
    if (!subserviceId) {
      setDetails(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      console.log(`🔄 Fetching service details for subservice ID: ${subserviceId} from NocoBase...`);
      const data = await nocobaseService.getServiceDetails(subserviceId, language);
      
      if (data) {
        console.log('✅ Successfully fetched service details from NocoBase:', data);
        setDetails(data);
      } else {
        console.warn('⚠️ No service details found for subservice ID:', subserviceId);
        setDetails(null);
      }
    } catch (err) {
      console.error('❌ Failed to fetch service details from NocoBase:', err);
      setError(err);
      setDetails(null);
    } finally {
      setLoading(false);
    }
  }, [subserviceId, language]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  return { details, loading, error, refetch: fetchDetails };
};

/**
 * Hook for fetching sub-service information along with its details
 * This combines subservice data with detailed information
 * @param {number|string} subserviceId - The ID of the sub-service
 * @param {string} language - Language code ('en' or 'ar')
 * @returns {Object} - { subservice, details, loading, error, refetch }
 */
export const useSubserviceWithDetails = (subserviceId, language = 'en') => {
  const [subservice, setSubservice] = useState(null);
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch all subservices to find the one we need
  const { subservices: allSubservices, loading: subservicesLoading } = useSubservices(null, true, language);
  
  // Fetch details
  const { details: serviceDetails, loading: detailsLoading, error: detailsError } = useServiceDetails(subserviceId, language);

  useEffect(() => {
    if (subserviceId && allSubservices && allSubservices.length > 0) {
      const found = allSubservices.find(s => 
        String(s.id) === String(subserviceId) || 
        String(s.Id) === String(subserviceId)
      );
      setSubservice(found || null);
    } else {
      setSubservice(null);
    }
  }, [subserviceId, allSubservices]);

  useEffect(() => {
    setDetails(serviceDetails);
  }, [serviceDetails]);

  useEffect(() => {
    setLoading(subservicesLoading || detailsLoading);
  }, [subservicesLoading, detailsLoading]);

  useEffect(() => {
    if (detailsError) {
      setError(detailsError);
    } else {
      setError(null);
    }
  }, [detailsError]);

  const refetch = useCallback(async () => {
    // This will trigger re-fetch through the hooks
    window.location.reload();
  }, []);

  return { 
    subservice, 
    details, 
    loading, 
    error, 
    refetch 
  };
};

/**
 * Hook for fetching service (main service) information along with its subservices
 * This combines service data with its related subservices
 * @param {number|string} serviceId - The ID of the main service
 * @param {string} language - Language code ('en' or 'ar')
 * @returns {Object} - { service, subservices, loading, error, refetch }
 */
export const useServiceWithDetails = (serviceId, language = 'en') => {
  const [service, setService] = useState(null);
  const [subservices, setSubservices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch all services to find the one we need
  const { services: allServices, loading: servicesLoading } = useServices(true, language);
  
  // Fetch all subservices to find related ones
  const { subservices: allSubservices, loading: subservicesLoading } = useSubservices(null, true, language);

  useEffect(() => {
    if (serviceId && allServices && allServices.length > 0) {
      const found = allServices.find(s => 
        String(s.id) === String(serviceId) || 
        String(s.Id) === String(serviceId)
      );
      setService(found || null);
    } else {
      setService(null);
    }
  }, [serviceId, allServices]);

  useEffect(() => {
    if (serviceId && allSubservices && allSubservices.length > 0) {
      console.log(`\n═══════════════════════════════════════════════════════════`);
      console.log(`🔍 FILTERING SUBSERVICES FOR SERVICE ID: ${serviceId}`);
      console.log(`═══════════════════════════════════════════════════════════`);
      console.log(`Total Subservices Available: ${allSubservices.length}`);
      console.log(`Service ID Type: ${typeof serviceId}`);
      console.log(`Service ID Value: ${serviceId}`);
      
      // Log ALL subservices with their linking fields
      console.log(`\n📋 ALL SUBSERVICES WITH LINKING FIELDS:`);
      allSubservices.forEach((sub, idx) => {
        console.log(`\n${idx + 1}. "${sub.title || sub.name}" (ID: ${sub.id})`);
        // IMPORTANT: Subservices is the CORRECT field name (bigInt Foreign Key)
        console.log(`   Subservices (CORRECT FIELD): ${sub.Subservices} (type: ${typeof sub.Subservices})`);
        console.log(`   parent_service_id: ${sub.parent_service_id} (type: ${typeof sub.parent_service_id})`);
        console.log(`   main_service_id: ${sub.main_service_id} (type: ${typeof sub.main_service_id})`);
        console.log(`   service_id: ${sub.service_id} (type: ${typeof sub.service_id})`);
        console.log(`   parent_service: ${sub.parent_service} (type: ${typeof sub.parent_service})`);
        
        // Check Subservices field first (the CORRECT field)
        let matchingField = null;
        let matchingValue = null;
        
        // Priority 1: Subservices field (bigInt - the CORRECT field)
        if (sub.Subservices !== undefined && sub.Subservices !== null) {
          const subServiceId = Number(sub.Subservices);
          const matches = (
            String(subServiceId) === String(serviceId) || 
            Number(subServiceId) === Number(serviceId) ||
            parseInt(subServiceId) === parseInt(serviceId)
          );
          if (matches) {
            matchingField = 'Subservices';
            matchingValue = subServiceId;
          }
        }
        
        // Fallback to normalized fields
        if (!matchingField) {
          const allLinkingFields = {
            parent_service_id: sub.parent_service_id,
            main_service_id: sub.main_service_id,
            service_id: sub.service_id,
            parent_service: sub.parent_service
          };
          
          for (const [fieldName, fieldValue] of Object.entries(allLinkingFields)) {
            if (fieldValue != null && fieldValue !== undefined) {
              const matches = (
                String(fieldValue) === String(serviceId) || 
                Number(fieldValue) === Number(serviceId) ||
                parseInt(fieldValue) === parseInt(serviceId)
              );
              if (matches) {
                matchingField = fieldName;
                matchingValue = fieldValue;
                break;
              }
            }
          }
        }
        
        if (matchingField) {
          console.log(`   ✅ MATCHES via ${matchingField}: ${matchingValue}`);
        } else {
          console.log(`   ❌ NO MATCH`);
        }
      });
      
      // Filter subservices that belong to this service
      // IMPORTANT: The CORRECT field name is "Subservices" (bigInt Foreign Key)
      const related = allSubservices.filter(sub => {
        // Priority 1: Subservices field (the CORRECT field according to schema)
        // This is a bigInt that contains the Service ID directly
        let subServiceId = null;
        
        if (sub.Subservices !== undefined && sub.Subservices !== null) {
          subServiceId = Number(sub.Subservices);
        }
        // Fallback to normalized fields for backward compatibility
        else if (sub.parent_service_id !== undefined && sub.parent_service_id !== null) {
          subServiceId = Number(sub.parent_service_id);
        }
        else if (sub.main_service_id !== undefined && sub.main_service_id !== null) {
          subServiceId = Number(sub.main_service_id);
        }
        else if (sub.service_id !== undefined && sub.service_id !== null) {
          subServiceId = Number(sub.service_id);
        }
        else if (sub.parent_service !== undefined && sub.parent_service !== null) {
          subServiceId = Number(sub.parent_service);
        }
        
        // Compare as strings and numbers
        if (subServiceId == null || subServiceId === undefined || isNaN(subServiceId) || subServiceId === 0) {
          return false;
        }
        
        // Try multiple comparison methods
        const serviceIdStr = String(serviceId);
        const serviceIdNum = Number(serviceId);
        const subServiceIdStr = String(subServiceId);
        const subServiceIdNum = Number(subServiceId);
        
        const matches = (
          subServiceIdStr === serviceIdStr || 
          subServiceIdNum === serviceIdNum ||
          parseInt(subServiceIdStr) === parseInt(serviceIdStr) ||
          parseFloat(subServiceIdStr) === parseFloat(serviceIdStr)
        );
        
        return matches;
      });
      
      console.log(`\n═══════════════════════════════════════════════════════════`);
      console.log(`🔗 RESULT: Found ${related.length} subservice(s) for service ID ${serviceId}`);
      console.log(`═══════════════════════════════════════════════════════════`);
      if (related.length > 0) {
        related.forEach((s, idx) => {
          console.log(`${idx + 1}. "${s.title || s.name}" (ID: ${s.id})`);
          console.log(`   Linking via: ${s.parent_service_id ? 'parent_service_id' : s.main_service_id ? 'main_service_id' : s.service_id ? 'service_id' : s.Subservices ? 'Subservices' : 'unknown'}`);
        });
      } else {
        console.log(`⚠️  NO SUBSERVICES FOUND!`);
        console.log(`   This might mean:`);
        console.log(`   1. The service has no subservices in the database`);
        console.log(`   2. The linking field name is incorrect`);
        console.log(`   3. The service ID doesn't match the linking field values`);
      }
      console.log(`═══════════════════════════════════════════════════════════\n`);
      
      setSubservices(related || []);
    } else {
      console.log(`⚠️ Cannot filter subservices:`, {
        hasServiceId: !!serviceId,
        hasSubservices: !!allSubservices,
        subservicesCount: allSubservices?.length || 0
      });
      setSubservices([]);
    }
  }, [serviceId, allSubservices]);

  useEffect(() => {
    setLoading(servicesLoading || subservicesLoading);
  }, [servicesLoading, subservicesLoading]);

  const refetch = useCallback(async () => {
    // This will trigger re-fetch through the hooks
    window.location.reload();
  }, []);

  return { 
    service, 
    subservices, 
    loading, 
    error, 
    refetch 
  };
};

